import type { Point2 } from "./flowField";

/** Upper bound on `<circle>` elements for flow-field dots mode (preview + export). */
export const MAX_FLOW_FIELD_DOTS = 50_000;

export interface FlowFieldDotSample {
    x: number;
    y: number;
    reactKey: string;
}

/** Minimum center-to-center distance: `max(extraGap, strokeWidth)` when `atLeastStroke`. */
export function getEffectiveDotMinGap(
    strokeWidth: number,
    extraGap: number,
    atLeastStroke: boolean,
): number {
    const g = Math.max(0, extraGap);
    if (atLeastStroke) return Math.max(g, strokeWidth);
    return g;
}

/**
 * Along each curve, keep a point only if it is at least `minGap` from the last kept point
 * (Euclidean distance between centers).
 */
function applyMinGapAlongCurves(
    curvePoints: Point2[][],
    minGap: number,
): FlowFieldDotSample[] {
    if (minGap <= 0) {
        const samples: FlowFieldDotSample[] = [];
        for (let ci = 0; ci < curvePoints.length; ci++) {
            const curve = curvePoints[ci];
            for (let pi = 0; pi < curve.length; pi++) {
                const p = curve[pi];
                samples.push({
                    x: p.x,
                    y: p.y,
                    reactKey: `${ci}-${pi}`,
                });
            }
        }
        return samples;
    }

    const minSq = minGap * minGap;
    const samples: FlowFieldDotSample[] = [];
    for (let ci = 0; ci < curvePoints.length; ci++) {
        const curve = curvePoints[ci];
        let last: Point2 | null = null;
        for (let pi = 0; pi < curve.length; pi++) {
            const p = curve[pi];
            if (last === null) {
                samples.push({
                    x: p.x,
                    y: p.y,
                    reactKey: `${ci}-${pi}`,
                });
                last = p;
                continue;
            }
            const dx = p.x - last.x;
            const dy = p.y - last.y;
            if (dx * dx + dy * dy >= minSq) {
                samples.push({
                    x: p.x,
                    y: p.y,
                    reactKey: `${ci}-${pi}`,
                });
                last = p;
            }
        }
    }
    return samples;
}

function applyGlobalCap(
    samples: FlowFieldDotSample[],
    maxDots: number,
): { samples: FlowFieldDotSample[]; capped: boolean } {
    const n = samples.length;
    if (maxDots <= 0 || n <= maxDots) {
        return { samples, capped: false };
    }
    const stride = Math.ceil(n / maxDots);
    const out: FlowFieldDotSample[] = [];
    for (let i = 0; i < n; i += stride) {
        out.push(samples[i]);
    }
    return { samples: out, capped: true };
}

/**
 * Build dot samples: optional minimum spacing along each curve, then optional global cap.
 */
export function buildFlowFieldDotSamples(
    curvePoints: Point2[][],
    maxDots: number,
    minGap: number,
): {
    samples: FlowFieldDotSample[];
    totalRaw: number;
    afterMinGap: number;
    capped: boolean;
} {
    const totalRaw = curvePoints.reduce((acc, c) => acc + c.length, 0);
    if (totalRaw === 0) {
        return {
            samples: [],
            totalRaw: 0,
            afterMinGap: 0,
            capped: false,
        };
    }

    const spaced = applyMinGapAlongCurves(curvePoints, minGap);
    const afterMinGap = spaced.length;
    const { samples, capped } = applyGlobalCap(spaced, maxDots);
    return { samples, totalRaw, afterMinGap, capped };
}
